import React, { useState, useMemo } from 'react';
import {
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';
import {
  TripRequest, ReferralType, CountrySetting, Destination,
} from '@marageti/z4-sdk/lib/travel';
import {
  Alert,
  AlertTitle,
  Skeleton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { SubmitHandler, FieldValues } from 'react-hook-form';
import TripRequestEditForm from '../trip-request-form';
import useTRFDestinations from '../../hooks/use-trf-destinations';

type TripRequestEditModalProps = {
  open: boolean,
  onClose: () => void,
  tripRequest?: TripRequest,
  onSuccess: () => void,
};

const TripRequestEditModal = ({
  open, onClose, tripRequest, onSuccess,
}: TripRequestEditModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const navigate = useNavigate();

  const tripRequestId = tripRequest?.id;

  const {
    data: trfDestinations, isLoading: isTRFDestinationsLoading, error: dataLoadError,
  } = useTRFDestinations();
  const { data: countrySettings, error: csError, isLoading: isCSLoading } = useFetchImmutable<CountrySetting[]>(apiClient.countrySettingsClient.getCountrySettings, [], true);
  const [submissionError, setSubmissionError] = useState<ApiError>();

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (tripRequest) {
      const {
        sendEmail, traveler, referringPartner, ...rest
      } = data;
      const tr: TripRequest = {
        ...tripRequest,
        ...rest,
      };
      if (referringPartner) {
        tr.referralId = referringPartner.id;
        tr.referralType = ReferralType.OneTimePartnerReferral;
      }
      await apiClient.tripRequestsClient.putTripRequest(tripRequestId!, tr)
        .then(() => {
          enqueueSnackbar('Trip request updated.', { variant: 'success' });
          onClose();
          onSuccess();
        })
        .catch((e: ApiError) => {
          if (e.code === 401) navigate('/logout');
          setSubmissionError(e);
        });
    }
  };

  const onCancel = () => onClose();

  const handleClose = (event: {}, reason?: string) => {
    if (reason !== 'backdropClick') {
      onClose();
    }
  };

  const defaultCountrySetting = useMemo(() => countrySettings?.find((cs) => cs.country === 'Default'), [countrySettings]);
  const defaultValues = useMemo(() => {
    if (!tripRequest || !countrySettings) return undefined;
    // Remove destinations with empty id
    const cleanDestinations = tripRequest.destinations.filter((d) => d.id !== '');

    const getMinNightsForDestinations = (destinations: Destination[]) => {
      if (!destinations.length) return defaultCountrySetting?.minNights || 5;

      const destinationMinNights = destinations.map((dest) => {
        const countryMinNights = dest.countries.map((iso) => {
          const cs = countrySettings?.find((c: CountrySetting) => c.isoCode2 === iso);
          return cs?.minNights || defaultCountrySetting?.minNights || 5;
        });
        return Math.max(...countryMinNights);
      });

      return Math.max(...destinationMinNights);
    };

    const calculatedMinNights = getMinNightsForDestinations(cleanDestinations);

    // Keep the original totalNights if it's greater than the calculated minNights
    const totalNights = tripRequest.totalNights
      ? Math.max(tripRequest.totalNights, calculatedMinNights)
      : calculatedMinNights;

    return {
      ...tripRequest,
      traveler: tripRequest.travelers[0],
      departureDate: tripRequest?.departureDate ? new Date(tripRequest.departureDate) : undefined,
      destinations: cleanDestinations.map((d) => ({
        ...d,
        minNights: getMinNightsForDestinations([d]),
      })),
      totalNights,
    };
  }, [tripRequest, countrySettings]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        p: 0,
        m: 0,
        '& .MuiDialog-paper': {
          width: '530px',
        },
      }}
    >
      <DialogTitle sx={{ pt: 4, px: 3, pb: 2.5 }}>
        Edit Request
      </DialogTitle>
      <DialogContent sx={{ p: 0, width: '100%', maxWidth: '600px' }}>
        {(dataLoadError || csError) && (
        <Alert severity="error">
          <AlertTitle>There was an error on our end.</AlertTitle>
          {`Code: ${(dataLoadError || csError).code}: ${(dataLoadError || csError).message}`}
          <br />
          {(dataLoadError || csError).id && `ID: ${(dataLoadError || csError).id}`}
        </Alert>
        )}
        {(isTRFDestinationsLoading || isCSLoading) ? (
          <Stack spacing={2} mt="20px">
            {Array(10).fill(null).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton key={index} variant="rounded" height="43px" width="100%" />
            ))}
          </Stack>
        ) : (
          <TripRequestEditForm
            destinationsList={trfDestinations || []}
            onCancel={onCancel}
            onSubmit={onSubmit}
            submissionError={submissionError}
            defaultValues={defaultValues}
            validateOnMount
            defaultCountrySettings={defaultCountrySetting}
            editMode
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TripRequestEditModal;
